import { Personality } from '../types';

export const validatePersonality = (personality: Personality): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Validar nombre
  if (!personality.name || personality.name.trim().length === 0) {
    errors.push('El nombre de la personalidad es requerido');
  }
  
  // Validar parámetros avanzados
  const params = personality.advanced_parameters;
  const paramKeys = ['empathy', 'formality', 'humor', 'verbosity', 'directness', 'creativity'] as const;
  
  for (const key of paramKeys) {
    const value = params[key];
    if (value === undefined || value < 0 || value > 1) {
      errors.push(`${key} debe estar entre 0 y 1`);
    }
  }
  
  // Validar configuración jerárquica
  if (personality.hierarchical_config?.enabled) {
    if (!personality.hierarchical_config.relationship_levels || 
        personality.hierarchical_config.relationship_levels.length === 0) {
      errors.push('La configuración jerárquica requiere al menos un nivel de relación');
    }
    
    // Validar rangos de afinidad
    const levels = personality.hierarchical_config.relationship_levels;
    for (let i = 0; i < levels.length; i++) {
      const level = levels[i];
      if (level.affinity_range[0] < 0 || level.affinity_range[1] > 100) {
        errors.push(`El nivel ${level.name} tiene un rango de afinidad inválido`);
      }
      
      if (level.affinity_range[0] >= level.affinity_range[1]) {
        errors.push(`El nivel ${level.name} tiene un rango de afinidad inválido (inicio >= fin)`);
      }
    }
  }
  
  // Validar configuración de memoria
  if (personality.memory_preferences) {
    if (!personality.memory_preferences.fact_categories || 
        personality.memory_preferences.fact_categories.length === 0) {
      errors.push('La configuración de memoria requiere al menos una categoría de hechos');
    }
    
    if (personality.memory_preferences.importance_threshold < 0 || 
        personality.memory_preferences.importance_threshold > 10) {
      errors.push('El umbral de importancia debe estar entre 0 y 10');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateComponent = (componentType: string, config: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  switch (componentType) {
    case 'traits':
      // Los rasgos se validan en validatePersonality
      break;
      
    case 'memory':
      if (!config.memory_preferences?.fact_categories || config.memory_preferences.fact_categories.length === 0) {
        errors.push('Configura al menos una categoría de memoria');
      }
      break;
      
    case 'affinity':
      if (!config.hierarchical_config?.relationship_levels || 
          config.hierarchical_config.relationship_levels.length === 0) {
        errors.push('El sistema de afinidad requiere al menos un nivel de relación');
      }
      break;
      
    case 'style':
      if (!config.linguistic_profile?.tone || config.linguistic_profile.tone.length === 0) {
        errors.push('El tono es requerido para el estilo de lenguaje');
      }
      break;
      
    case 'safety':
      if (!config.safety_guards?.forbidden_topics || config.safety_guards.forbidden_topics.length === 0) {
        errors.push('Define al menos un tema sensible para las reglas de seguridad');
      }
      break;
      
    default:
      errors.push(`Tipo de componente desconocido: ${componentType}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const getPersonalityCompleteness = (personality: Personality): number => {
  let score = 0;
  const maxScore = 5;
  
  // Rasgos base (siempre presente)
  score += 1;
  
  // Sistema de memoria
  if (personality.memory_preferences) {
    score += 1;
  }
  
  // Sistema de afinidad
  if (personality.hierarchical_config?.enabled) {
    score += 1;
  }
  
  // Estilo de lenguaje
  if (personality.linguistic_profile?.tone && personality.linguistic_profile.tone.length > 0) {
    score += 1;
  }
  
  // Reglas de seguridad
  if (personality.safety_guards?.forbidden_topics && personality.safety_guards.forbidden_topics.length > 0) {
    score += 1;
  }
  
  return Math.round((score / maxScore) * 100);
};

export const getRecommendedComponents = (personality: Personality): string[] => {
  const recommendations: string[] = [];
  
  if (!personality.memory_preferences) {
    recommendations.push('memory');
  }
  
  if (!personality.hierarchical_config?.enabled) {
    recommendations.push('affinity');
  }
  
  if (!personality.linguistic_profile?.tone || personality.linguistic_profile.tone.length === 0) {
    recommendations.push('style');
  }
  
  if (!personality.safety_guards?.forbidden_topics || personality.safety_guards.forbidden_topics.length === 0) {
    recommendations.push('safety');
  }
  
  return recommendations;
};
